import { useState, useEffect } from "react";

/**
 * Custom hook to detect online/offline network status.
 *
 * Uses the Navigator.onLine API to track network connectivity.
 * Listens to 'online' and 'offline' events to update status in real-time.
 *
 * @returns {boolean} isOnline - true if the device has network connectivity
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const isOnline = useNetworkStatus();
 *
 *   if (!isOnline) {
 *     return <div>You are offline</div>;
 *   }
 *
 *   return <div>You are online</div>;
 * };
 * ```
 *
 * Note: navigator.onLine is not 100% reliable (may show "online" with no actual internet)
 * but it's good enough for most use cases and provides instant feedback on connection loss.
 */
export function useNetworkStatus(): boolean {
  // Initialize with current online status
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    // Handler for when connection is restored
    const handleOnline = () => {
      console.log('[Network] Connection restored');
      setIsOnline(true);
    };

    // Handler for when connection is lost
    const handleOffline = () => {
      console.log('[Network] Connection lost');
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
